<template>
  <section
    class="desktop-dept-mega"
    :class="{ open: isOpen }"
    id="dept-dropdown"
    :aria-label="$t('departmentMenu', { name: menuData.name })"
    @keydown.esc="emit('close')"
  >
    <div class="mega-header">
      <h2 class="mega-header-title">{{ menuData.name }}</h2>
      <button type="button" class="mega-header-link" @click="onVisitHome">
        <font-awesome-icon icon="house" />
        {{ $t('visitDepartmentHome', { name: menuData.name }) }}
      </button>
    </div>

    <div class="mega-body">
      <div class="mega-left" id="mega-left">
        <button
          v-for="(dept, idx) in menuData.children"
          :key="dept.id"
          type="button"
          class="mega-dept"
          :class="{ 'is-active': activeIndex === idx }"
          @mouseenter="activeIndex = idx"
          @click="onMenuItemClick(dept)"
        >
          {{ dept.name }}
        </button>
      </div>

      <div v-if="rightPanelVisible" class="mega-right" id="mega-right">
        <div class="mega-cat-panel">
          <h3 class="mega-cat-title">
            {{ $t('browse') }} <span>{{ activeDept.name }}</span>
          </h3>

          <div v-if="hasCategories" class="mega-cat-cols">
            <div v-for="group in nestedChildren" :key="group.id" class="mega-cat-col">
              <h3 v-if="group.name" class="mega-cat-col-title">{{ group.name }}</h3>
              <button
                v-for="child in group.children"
                :key="child.id"
                type="button"
                class="mega-cat-link"
                @click="onSubcategoryClick(group, child)"
              >
                {{ child.name }}
              </button>
            </div>

            <div v-if="singleChildren.length" class="mega-cat-col">
              <button
                v-for="child in singleChildren"
                :key="child.id"
                type="button"
                class="mega-cat-link"
                @click="onSubcategoryClick(null, child)"
              >
                {{ child.name }}
              </button>
            </div>
          </div>

          <button type="button" class="mega-view-all" @click="onViewAll(activeDept)">
            {{ $t('viewAllDepartment', { name: activeDept.name }) }}
            <font-awesome-icon icon="arrow-right" />
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { MenuNode } from '@/models/entities/menu-node';

interface Props {
  menuData: MenuNode;
  isOpen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
});

const emit = defineEmits<{
  selectSubcategory: [payload: { parent: MenuNode | null; child: MenuNode }];
  viewAll: [dept: MenuNode];
  visitHome: [menu: MenuNode];
  close: [];
}>();

const activeIndex = ref(0);

const activeDept = computed(() => props.menuData.children?.[activeIndex.value] ?? null);

const nestedChildren = computed(
  () => activeDept.value?.children?.filter(child => child?.children?.length > 0) ?? [],
);

const singleChildren = computed(
  () => activeDept.value?.children?.filter(child => child && !child.children?.length) ?? [],
);

const hasCategories = computed(
  () => nestedChildren.value.length > 0 || singleChildren.value.length > 0,
);

const rightPanelVisible = computed(() => Boolean(activeDept.value));

function onMenuItemClick(dept: MenuNode): void {
  if (!dept.children || !dept.children.length) {
    emit('selectSubcategory', { parent: null, child: dept });
  }
}

function onSubcategoryClick(group: MenuNode | null, child: MenuNode): void {
  emit('selectSubcategory', { parent: group, child });
}

function onViewAll(dept: MenuNode): void {
  emit('viewAll', dept);
}

function onVisitHome(): void {
  emit('visitHome', props.menuData);
}

watch(
  () => props.menuData,
  () => {
    activeIndex.value = 0;
  },
);
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.desktop-dept-mega {
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 500;
  background: var(--white, $white);
  border: 0.0625rem solid $border-color-light;
  border-radius: 0 0.5rem 0.5rem 0.5rem;
  box-shadow: 0 0.5rem 1.5rem rgba($black, 0.15);
  min-width: 40rem;

  &.open {
    display: flex;
    flex-direction: column;
  }
}

.mega-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.25rem;
  border-bottom: 0.0625rem solid $border-color-lighter;
}

.mega-header-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--primary, $primary);
}

.mega-header-link {
  font-size: 0.75rem;
  font-weight: 600;
  color: $link-color-alt;
  cursor: pointer;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    text-decoration: underline;
  }
}

.mega-body {
  display: flex;
  min-height: 17.5rem;
  max-height: 22.5rem;
}

.mega-left {
  width: 12.5rem;
  border-right: 0.0625rem solid $border-color-lighter;
  padding: 0.5rem 0;
  flex-shrink: 0;
  min-height: 0;
  overflow-y: auto;
}

.mega-dept {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5625rem 1rem;
  font-size: 0.8125rem;
  color: var(--dark-darker-2);
  cursor: pointer;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  border-left: 0.1875rem solid transparent;
  transition: all 0.1s;

  &:hover,
  &.is-active {
    background: var(--primary-lighter-9, get-color('primary', 'lighter-9'));
    color: var(--primary, $primary);
    border-left-color: var(--primary, $primary);
    font-weight: 600;
  }

  &:hover,
  &:active {
    text-decoration: underline;
  }
}

.mega-right {
  flex: 1;
  padding: 1rem 1.25rem;
  min-height: 0;
  overflow-y: auto;
}

.mega-cat-panel {
  display: block;
}

.mega-cat-title {
  margin: 0 0 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.0375rem;
  color: var(--primary, $primary);
  display: flex;
  align-items: center;
  gap: 0.375rem;

  span {
    font-size: 1rem;
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0;
  }
}

.mega-cat-cols {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.75rem;
}

.mega-cat-col {
  min-width: 0;
}

.mega-cat-col-title {
  margin: 0 0 0.5rem;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.0375rem;
  color: $text-color-muted;
}

.mega-cat-link {
  display: block;
  width: 100%;
  font-size: 0.8125rem;
  color: var(--dark-darker-2);
  padding: 0.25rem 0;
  cursor: pointer;
  background: none;
  border: none;
  text-align: left;
  text-decoration: none;
  transition: color 0.1s;

  &:hover {
    color: var(--primary, $primary);
    text-decoration: underline;
  }
}

.mega-view-all {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: $link-color-alt;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
}
</style>
